import { ActivityComponentType } from '@stackflow/react'
import classNames from 'classnames'
import { useEffect, useRef, useState } from 'react'
import { useEventTracking } from '../../../helpers/gtmHelper'
import { useLessonInfo } from '../../../modules/apis/student/lesson/review/queries'
import { IScheduleResult } from '../../../modules/apis/student/lesson/schedule/queries'
import { useCurrentUser } from '../../../modules/context/UserContext'
import useAppRouter from '../../../modules/hooks/common/useAppRouter'
import { n_lesson_review } from '../../../modules/i18n/strings/auto/wording'
import useTranslation from '../../../modules/i18n/useTranslation'
import { useLessonsStack } from '../../../pages/[lang]/webview/lessons'
import CAFOverview from '../../new-portal/caf/CAFOverview'
import CompletedLesson from '../../new-portal/lessons/schedules/CompletedLesson'
import { DocNoteSnapshotReviewTabPanel as Note } from '../../new-portal/review/DocNoteTabPanel'
import Feedback from '../../new-portal/review/Feedback'
import RecordedClassReview from '../../new-portal/review/RecordedClassReview'
import { REVIEW_TABS } from '../../new-portal/review/contants'
import LessonInfoTitle from '../common/LessonInfoTitle'
import WebviewStackContainer from '../common/WebviewStackContainer'

const getIndex = (type: string | string[] | number): REVIEW_TABS => {
  if (type === 'caf' || type == REVIEW_TABS.AI) return REVIEW_TABS.AI
  else if (type === 'feedback' || type == REVIEW_TABS.FEEDBACK) return REVIEW_TABS.FEEDBACK
  else if (type === 'note' || type == REVIEW_TABS.NOTE) return REVIEW_TABS.NOTE
  else if (type === 'record' || type == REVIEW_TABS.RECORD) return REVIEW_TABS.RECORD
  else return REVIEW_TABS.AI
}

const ReviewDetailActivity: ActivityComponentType = () => {
  const { query } = useAppRouter()
  const [result, setResult] = useState<IScheduleResult>(null)
  const { useLessonsFlow } = useLessonsStack()
  const { pop } = useLessonsFlow()
  const { sendPageViewEvent } = useEventTracking()
  const { lessonId } = query
  const router = useAppRouter()
  const { currentUser } = useCurrentUser()
  const routeQueryType = router.query?.type
  const [activeTab, setActiveTab] = useState(0)
  const { data: lessonInfo } = useLessonInfo(Number(lessonId))

  useEffect(() => {
    if (getIndex(routeQueryType) !== activeTab) {
      setActiveTab(getIndex(routeQueryType))
    }
  }, [routeQueryType])

  useEffect(() => {
    if (!router) return
    console.log('SEND GTM EVENT TEST', activeTab)
    if (activeTab === 0) {
      sendPageViewEvent({ page_name: 'lesson_review_ai_analysis_overview' })
    } else if (activeTab === 1) {
      sendPageViewEvent({ page_name: 'lesson_review_tutor_feedback' })
    } else if (activeTab === 2) {
      sendPageViewEvent({ page_name: 'lesson_review_record' })
    } else if (activeTab === 3) {
      sendPageViewEvent({ page_name: 'lesson_review_note' })
    }
  }, [activeTab, router])

  if (!lessonInfo) return null
  return (
    // 최상위 div 지우면 하위컴포넌트 position:fixed 안됨
    <div>
      <WebviewStackContainer
        height="52px"
        onBack={pop}
        border={false}
        title={<LessonInfoTitle lessonId={Number(lessonId)} />}
      >
        <SubMenu activeIndex={activeTab} setActiveIndex={setActiveTab} />
        {result ? (
          <div className="w-full max-w-[976px] py-[32px] px-[16px] lg:max-w-[656px] mx-auto">
            <CompletedLesson result={result} />
          </div>
        ) : (
          <div className="h-screen">
            <ComponentSwitch
              index={activeTab}
              lessonId={lessonId}
              currentUser={currentUser}
              lesson={lessonInfo}
              setResult={setResult}
            />
          </div>
        )}
      </WebviewStackContainer>
    </div>
  )
}

const ComponentSwitch = ({ index, lesson, lessonId, currentUser, setResult }) => {
  const iframeRef = useRef(null)
  if (index === 3) {
    return (
      <Note
        documentUrl={lesson?.google_doc}
        ringledocUrl={lesson?.ringledoc_url}
        iframeRef={iframeRef}
        ringleDocToken={lesson?.ringledoc}
        specialLesson={lesson?.special_lesson}
        currentUser={currentUser}
        height={'100%'}
        style={'border-0 h-full w-full h-full pb-[200px]'}
      />
    )
  } else if (index === 1) {
    return (
      <div className="px-[20px]">
        <Feedback lessonId={lessonId} setResult={setResult} />
      </div>
    )
  } else if (index === 0) {
    return <CAFOverview setResult={setResult} />
  } else {
    return <RecordedClassReview currentUser={currentUser} lessonId={lessonId} />
  }
}

interface SubmenuProps {
  activeIndex: REVIEW_TABS
  setActiveIndex: (tabIndex: number) => void
}

const SubMenu = ({ activeIndex, setActiveIndex }: SubmenuProps) => {
  const { t } = useTranslation()
  const MENU = [
    {
      label: t(n_lesson_review.tab1),
    },
    {
      label: t(n_lesson_review.tab2),
    },
    {
      label: t(n_lesson_review.tab3),
    },
    {
      label: t(n_lesson_review.tab4),
    },
  ]

  return (
    <div className="border-b-1 no-scrollbar sticky top-[0] left-0 z-50 flex h-[52px] w-screen flex-row items-center justify-center overflow-x-auto overflow-y-hidden border-gray-200 bg-white sm:justify-center lg:justify-start lg:pl-[120px] xl:justify-center min-h-[52px]">
      <div className="mx-[15px] flex w-auto flex-row items-center justify-between px-[16px] sm:w-[640px] sm:px-[32px] lg:w-[800px] xl:w-[1040px] xl:px-0">
        <div className="flex w-[100vw] flex-row items-start overflow-auto">
          {MENU.map((menu, i) => {
            return (
              <div
                className={classNames(
                  'no-scrollbar mx-[4px] cursor-pointer whitespace-nowrap px-[15px] py-[13px] font-bold',
                  {
                    'border-b-2 border-gray-700 text-black': activeIndex === i,
                    'border-b-2 border-white text-gray-300': activeIndex !== i,
                  }
                )}
                onClick={() => {
                  setActiveIndex(i)
                }}
                key={`${menu.label}_${i}`}
              >
                {menu.label}
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

export default ReviewDetailActivity
